import type {
  AdvisorMovement,
  AdvisorMovementKind,
  AppNotification,
  ApprovalStatus,
  Client,
  DeliveryMode,
  Distributor,
  Encargo,
  EncargoStatus,
  Expense,
  ExpenseCategory,
  ExpenseKind,
  ExpenseStatus,
  FundKey,
  FundMovement,
  ImportClientRef,
  ImportInventorySnapshot,
  ImportLog,
  ImportProductRef,
  ImportSaleRef,
  InventoryAdjustment,
  InventoryItem,
  KitItem,
  Loan,
  LoanType,
  Product,
  Promotion,
  Receivable,
  ReceivablePayment,
  RestockItem,
  Role,
  Sale,
  SaleOrigin,
  SaleType,
  Settings,
  StockEntry,
  TeamMember,
  Wallet,
} from "@/types";

// Conversión entre las filas de Supabase (snake_case) y los tipos de la app (camelCase).

type Row = Record<string, unknown>;

export const num = (v: unknown): number => (v == null ? 0 : Number(v) || 0);
export const str = (v: unknown): string => (v == null ? "" : String(v));

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ¿El registro es nuevo (aún sin id real de la base)? Cualquier id que no sea un
// UUID (vacío, "new_x", "rec_x", "promo_x"…) se considera nuevo y se INSERTA.
// Antes solo se miraba el prefijo "new_", así que los ids tipo uid("rec") se
// trataban como edición de un registro inexistente y el guardado fallaba en silencio.
export const isNewId = (id?: string): boolean => !id || !UUID_RE.test(id);

// Acepta tanto el formato nuevo ([{ amount, date }]) como el viejo ([num,num,num])
// para no perder abonos ya registrados al migrar.
const toPayments = (v: unknown): ReceivablePayment[] => {
  if (!Array.isArray(v)) return [];
  return v
    .map((raw) => {
      if (typeof raw === "number") return { amount: num(raw), date: "" };
      const r = (raw ?? {}) as Row;
      return { amount: num(r.amount), date: str(r.date) };
    })
    .filter((p) => p.amount > 0);
};

const toAdvisorMap = (v: unknown): Record<string, number> => {
  if (!v || typeof v !== "object" || Array.isArray(v)) return {};
  const out: Record<string, number> = {};
  Object.entries(v as Record<string, unknown>).forEach(([k, val]) => {
    out[k] = num(val);
  });
  return out;
};

// ---------------------------------------------------------------- Product
export const rowToProduct = (r: Row): Product => ({
  id: str(r.id),
  sku: str(r.sku),
  name: str(r.name),
  category: str(r.category) || "Hogar",
  pv: num(r.pv),
  costBase: num(r.cost_base),
  pricePublic: num(r.price_public),
  imageUrl: r.image_url ? str(r.image_url) : undefined,
});

export const productToRow = (p: Product): Row => ({
  sku: p.sku,
  name: p.name,
  category: p.category,
  pv: p.pv,
  cost_base: p.costBase,
  price_public: p.pricePublic,
  image_url: p.imageUrl ?? null,
});

// ------------------------------------------------------------- Distributor
const toApprovalStatus = (v: unknown): ApprovalStatus => {
  const s = str(v);
  if (s === "pending" || s === "rejected" || s === "suspended") return s;
  return "approved";
};

export const rowToDistributor = (r: Row): Distributor => ({
  id: str(r.id),
  role: str(r.role) === "admin" ? "admin" : "asesor",
  approvalStatus: toApprovalStatus(r.approval_status),
  names: str(r.names),
  surnames: str(r.surnames),
  cedula: str(r.cedula),
  phone: str(r.phone),
  email: str(r.email),
  address: str(r.address),
  city: str(r.city),
  margin: (num(r.margin) === 20 ? 20 : 25) as 20 | 25,
  consignmentLimit: num(r.consignment_limit),
  idFront: r.id_front ? str(r.id_front) : undefined,
  idBack: r.id_back ? str(r.id_back) : undefined,
  reference: {
    name: str(r.ref_name),
    relationship: str(r.ref_relationship),
    phone: str(r.ref_phone),
    city: str(r.ref_city),
  },
  createdAt: str(r.created_at),
});

// ------------------------------------------------------------------ Team
export const rowToTeamMember = (r: Row): TeamMember => ({
  id: str(r.id),
  role: str(r.role) === "admin" ? "admin" : "asesor",
  approvalStatus: toApprovalStatus(r.approval_status),
  names: str(r.names),
  surnames: str(r.surnames),
  cedula: str(r.cedula),
  phone: str(r.phone),
  email: str(r.email),
  city: str(r.city),
  address: str(r.address),
  createdAt: str(r.created_at),
});

export const distributorToRow = (d: Distributor): Row => ({
  role: "asesor",
  names: d.names,
  surnames: d.surnames,
  cedula: d.cedula,
  phone: d.phone,
  email: d.email,
  address: d.address,
  city: d.city,
  margin: d.margin,
  consignment_limit: d.consignmentLimit,
  id_front: d.idFront ?? null,
  id_back: d.idBack ?? null,
  ref_name: d.reference.name,
  ref_relationship: d.reference.relationship,
  ref_phone: d.reference.phone,
  ref_city: d.reference.city,
});

// ---------------------------------------------------------------- Client
export const rowToClient = (r: Row): Client => ({
  id: str(r.id),
  name: str(r.name),
  cedula: str(r.cedula),
  phone: str(r.phone),
  address: str(r.address),
  department: str(r.department),
  city: str(r.city),
  neighborhood: str(r.neighborhood),
});

export const clientToRow = (c: Client): Row => ({
  name: c.name,
  cedula: c.cedula,
  phone: c.phone,
  address: c.address,
  department: c.department ?? "",
  city: c.city ?? "",
  neighborhood: c.neighborhood ?? "",
});

// ----------------------------------------------------------------- Sale
export const rowToSale = (r: Row): Sale => ({
  id: str(r.id),
  date: str(r.date),
  clientId: str(r.client_id),
  clientName: str(r.client_name),
  productId: str(r.product_id),
  productName: str(r.product_name),
  qty: num(r.qty),
  pv: num(r.pv),
  total: num(r.total),
  marginPct: num(r.margin_pct),
  advisorId: str(r.advisor_id),
  type: (str(r.type) || "Full") as SaleType,
  origin: (str(r.origin) || "Casa") as SaleOrigin,
  delivery: (str(r.delivery) || "recojo") as DeliveryMode,
  historical: Boolean(r.historical),
  source:
    str(r.source) === "consignado" || str(r.source) === "propio"
      ? (str(r.source) as "consignado" | "propio")
      : null,
});

export const saleToRow = (s: Sale): Row => ({
  date: s.date,
  client_id: s.clientId || null,
  client_name: s.clientName,
  product_id: s.productId || null,
  product_name: s.productName,
  qty: s.qty,
  pv: s.pv,
  total: s.total,
  margin_pct: s.marginPct,
  type: s.type,
  origin: s.origin,
  delivery: s.delivery,
  historical: s.historical ?? false,
  source: s.source ?? null,
});

// La venta DIRECTA (Juanka/Admin) se guarda en la base con el uuid del perfil del
// admin (por defecto auth.uid()), no con la palabra "admin". Al leer, cualquier
// venta que NO pertenezca a un asesor conocido es una venta directa del admin, así
// que la volvemos a marcar como "admin" para que el reparto del 25%, la ganancia y
// los fondos la reconozcan como venta propia.
export const normalizeSale = (sale: Sale, advisorIds: Set<string>): Sale =>
  sale.advisorId && advisorIds.has(sale.advisorId)
    ? sale
    : { ...sale, advisorId: "admin" };

// ------------------------------------------------------------ Inventory
export const rowToInventory = (r: Row): InventoryItem => ({
  productId: str(r.product_id),
  stockCasa: num(r.stock_casa),
  stockPuntoClear: num(r.stock_punto_clear),
  stockByAdvisor: toAdvisorMap(r.stock_by_advisor),
  stockOwnedByAdvisor: toAdvisorMap(r.owned_by_advisor),
});

export const inventoryToRow = (i: InventoryItem): Row => ({
  product_id: i.productId,
  stock_casa: i.stockCasa,
  stock_punto_clear: i.stockPuntoClear,
  stock_by_advisor: i.stockByAdvisor,
  owned_by_advisor: i.stockOwnedByAdvisor,
});

// ---------------------------------------------------- Advisor movement
const MOVEMENT_KINDS: AdvisorMovementKind[] = [
  "consignacion",
  "compra",
  "venta",
  "pago",
  "fee",
  "devolucion",
];

export const rowToAdvisorMovement = (r: Row): AdvisorMovement => ({
  id: str(r.id),
  advisorId: str(r.advisor_id),
  productId: str(r.product_id),
  productName: str(r.product_name),
  kind: (MOVEMENT_KINDS.includes(str(r.kind) as AdvisorMovementKind)
    ? str(r.kind)
    : "consignacion") as AdvisorMovementKind,
  qty: num(r.qty),
  unitValue: num(r.unit_value),
  amount: num(r.amount),
  note: str(r.note),
  date: str(r.date),
});

export const advisorMovementToRow = (m: AdvisorMovement): Row => ({
  advisor_id: m.advisorId,
  product_id: m.productId || null,
  product_name: m.productName,
  kind: m.kind,
  qty: m.qty,
  unit_value: m.unitValue,
  amount: m.amount,
  note: m.note,
  date: m.date || new Date().toISOString(),
});

// ------------------------------------------------------------- Expense
export const rowToExpense = (r: Row): Expense => ({
  id: str(r.id),
  date: str(r.date),
  concept: str(r.concept),
  category: (str(r.category) || "Gasto operativo") as ExpenseCategory,
  wallet: (str(r.wallet) || "Efectivo") as Wallet,
  amount: num(r.amount),
  note: str(r.note),
  kind: (str(r.kind) === "compra" ? "compra" : "operativo") as ExpenseKind,
  status: (
    str(r.status) === "pendiente" || str(r.status) === "parcial" ? str(r.status) : "pagado"
  ) as ExpenseStatus,
  paidAmount: r.paid_amount == null ? num(r.amount) : num(r.paid_amount),
  fund: str(r.fund) ? (str(r.fund) as FundKey) : null,
  productId: r.product_id ? str(r.product_id) : null,
});

export const expenseToRow = (e: Expense): Row => ({
  date: e.date || new Date().toISOString(),
  concept: e.concept,
  category: e.category,
  wallet: e.wallet,
  amount: e.amount,
  note: e.note,
  kind: e.kind,
  status: e.status,
  paid_amount: e.paidAmount,
  fund: e.fund ?? null,
  product_id: e.productId ?? null,
});

// ---------------------------------------------------------- Receivable
export const rowToReceivable = (r: Row): Receivable => ({
  id: str(r.id),
  date: str(r.date),
  clientName: str(r.client_name),
  productName: str(r.product_name),
  initialValue: num(r.initial_value),
  payments: toPayments(r.payments),
});

export const receivableToRow = (x: Receivable): Row => ({
  date: x.date,
  client_name: x.clientName,
  product_name: x.productName,
  initial_value: x.initialValue,
  payments: x.payments,
});

// ----------------------------------------------------------------- Loan
export const rowToLoan = (r: Row): Loan => ({
  id: str(r.id),
  date: str(r.date),
  concept: str(r.concept),
  value: num(r.value),
  type: (str(r.type) || "Préstamo") as LoanType,
});

export const loanToRow = (l: Loan): Row => ({
  date: l.date,
  concept: l.concept,
  value: l.value,
  type: l.type,
});

// ----------------------------------------------------------- Promotion
export const rowToPromotion = (r: Row): Promotion => ({
  id: str(r.id),
  title: str(r.title),
  description: str(r.description),
  discountPct: num(r.discount_pct),
  startDate: str(r.start_date),
  endDate: str(r.end_date),
  createdAt: str(r.created_at),
  items: Array.isArray(r.items)
    ? (r.items as Row[])
        .map((it) => ({
          productId: str(it.productId ?? it.product_id),
          qty: num(it.qty) || 1,
        }))
        .filter((it) => it.productId)
    : [],
});

export const promotionToRow = (p: Promotion): Row => ({
  title: p.title,
  description: p.description,
  discount_pct: p.discountPct,
  start_date: p.startDate || null,
  end_date: p.endDate || null,
  items: (p.items ?? []).map((i) => ({ productId: i.productId, qty: i.qty })),
});

// ------------------------------------------------------------- Encargo
export const rowToEncargo = (r: Row): Encargo => ({
  id: str(r.id),
  advisorId: str(r.advisor_id),
  advisorName: str(r.advisor_name),
  productId: str(r.product_id),
  productName: str(r.product_name),
  qty: num(r.qty),
  unitPrice: num(r.unit_price),
  note: str(r.note),
  delivery: (str(r.delivery) === "domicilio" ? "domicilio" : "recojo") as DeliveryMode,
  logisticsFee: num(r.logistics_fee),
  status: (str(r.status) || "pendiente") as EncargoStatus,
  date: str(r.date),
  deliverMode:
    str(r.deliver_mode) === "compra" || str(r.deliver_mode) === "consignacion"
      ? (str(r.deliver_mode) as "compra" | "consignacion")
      : null,
  deliverWallet: r.deliver_wallet ? (str(r.deliver_wallet) as Wallet) : null,
  unitCostBase: r.unit_cost_base == null ? null : num(r.unit_cost_base),
  unitCostAdvisor: r.unit_cost_advisor == null ? null : num(r.unit_cost_advisor),
});

export const encargoToRow = (e: Encargo): Row => ({
  advisor_id: e.advisorId,
  advisor_name: e.advisorName,
  product_id: e.productId || null,
  product_name: e.productName,
  qty: e.qty,
  unit_price: e.unitPrice,
  note: e.note,
  delivery: e.delivery,
  logistics_fee: e.logisticsFee,
  status: e.status,
  date: e.date || new Date().toISOString(),
  deliver_mode: e.deliverMode ?? null,
  deliver_wallet: e.deliverWallet ?? null,
  unit_cost_base: e.unitCostBase ?? null,
  unit_cost_advisor: e.unitCostAdvisor ?? null,
});

// ---------------------------------------------------------- Restock item
export const rowToRestockItem = (r: Row): RestockItem => ({
  id: str(r.id),
  advisorId: str(r.advisor_id),
  productId: str(r.product_id),
  productName: str(r.product_name),
  qty: num(r.qty),
  note: str(r.note),
  createdAt: str(r.created_at),
});

export const restockItemToRow = (i: RestockItem): Row => ({
  advisor_id: i.advisorId,
  product_id: i.productId || null,
  product_name: i.productName,
  qty: i.qty,
  note: i.note,
});

// ------------------------------------------------------------- Kit items
const toKitItems = (v: unknown): KitItem[] => {
  if (!Array.isArray(v)) return [];
  return v.map((raw, idx) => {
    const r = (raw ?? {}) as Row;
    return {
      id: str(r.id) || `kit_${idx}`,
      title: str(r.title),
      text: str(r.text),
      imageUrl: str(r.imageUrl) || str(r.image_url),
      createdAt: str(r.createdAt),
    };
  });
};

// -------------------------------------------------------- Notification
export const rowToNotification = (r: Row): AppNotification => ({
  id: str(r.id),
  audience: (str(r.audience) || "all") as AppNotification["audience"],
  title: str(r.title),
  message: str(r.message),
  kind: (str(r.kind) || "info") as AppNotification["kind"],
  date: str(r.date),
  read: Boolean(r.read),
  link: r.link ? str(r.link) : null,
});

export const notificationToRow = (n: Omit<AppNotification, "id">): Row => ({
  audience: n.audience,
  title: n.title,
  message: n.message,
  kind: n.kind,
  date: n.date,
  read: n.read,
  link: n.link ?? null,
});

// ------------------------------------------------------------ Settings
const toWalletBalances = (v: unknown): Record<Wallet, number> => {
  const base: Record<Wallet, number> = {
    Bancolombia: 0,
    Daviplata: 0,
    Nequi: 0,
    "Caja Social": 0,
    Efectivo: 0,
  };
  if (v && typeof v === "object" && !Array.isArray(v)) {
    Object.entries(v as Record<string, unknown>).forEach(([k, val]) => {
      base[k as Wallet] = num(val);
    });
  }
  return base;
};

// -------------------------------------------------------- Fund movements
const toReferralMap = (v: unknown): Record<string, number> => {
  if (!v || typeof v !== "object" || Array.isArray(v)) return {};
  const out: Record<string, number> = {};
  Object.entries(v as Record<string, unknown>).forEach(([k, val]) => {
    out[k] = num(val);
  });
  return out;
};

const toFundMovements = (v: unknown): FundMovement[] => {
  if (!Array.isArray(v)) return [];
  return v.map((raw, idx) => {
    const r = (raw ?? {}) as Row;
    const kind = str(r.kind);
    return {
      id: str(r.id) || `fm_${idx}`,
      date: str(r.date),
      kind: (
        kind === "prestamo_juank" || kind === "pago_prestamo" ? kind : "transferencia"
      ) as FundMovement["kind"],
      fromFund: str(r.fromFund) ? (str(r.fromFund) as FundKey) : null,
      toFund: str(r.toFund) ? (str(r.toFund) as FundKey) : null,
      amount: num(r.amount),
      note: str(r.note),
    };
  });
};

export const rowToSettings = (r: Row | null): Settings => ({
  walletBalances: toWalletBalances(r?.wallet_balances),
  logoUrl: r ? str(r.logo_url) : "",
  logisticsFee: r ? num(r.logistics_fee) : 12000,
  distribution: {
    donation: r ? num((r.distribution as Row)?.donation) : 2,
    advisor: r ? num((r.distribution as Row)?.advisor) : 5,
    reinvestment: r ? num((r.distribution as Row)?.reinvestment) : 13,
    management: r ? num((r.distribution as Row)?.management) : 5,
  },
  titleStyle: {
    font: r ? str((r.title_style as Row)?.font) || "Sora" : "Sora",
    color: r ? str((r.title_style as Row)?.color) || "#FFFFFF" : "#FFFFFF",
    glow: r ? Boolean((r.title_style as Row)?.glow) : true,
  },
  kitItems: toKitItems(r?.kit_items),
  fundMovements: toFundMovements(r?.fund_movements),
  categories: Array.isArray(r?.categories)
    ? (r?.categories as unknown[]).map((c) => str(c)).filter(Boolean)
    : undefined,
  referralPvByMonth: (() => {
    const map = toReferralMap(r?.referral_pv_by_month);
    // Compatibilidad: si aún no hay mapa mensual pero existía el valor único
    // antiguo (referral_pv), lo sembramos en el mes actual para no perderlo.
    if (Object.keys(map).length === 0 && num(r?.referral_pv) > 0) {
      const now = new Date();
      const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      map[key] = num(r?.referral_pv);
    }
    return map;
  })(),
});

// --------------------------------------------------------- Stock entries (PV)
// Registro de PV generado al ingresar mercancía al stock propio (por compra).
// Las ventas NO vuelven a sumar PV.
export const rowToStockEntry = (r: Row): StockEntry => ({
  id: str(r.id),
  date: str(r.date),
  productId: str(r.product_id),
  productName: str(r.product_name),
  qty: num(r.qty),
  pvPerUnit: num(r.pv_per_unit),
  pvTotal: num(r.pv_total),
  source: str(r.source),
  note: str(r.note),
});

export const stockEntryToRow = (e: StockEntry): Row => ({
  date: e.date || new Date().toISOString(),
  product_id: e.productId || null,
  product_name: e.productName,
  qty: e.qty,
  pv_per_unit: e.pvPerUnit,
  pv_total: e.pvTotal,
  source: e.source,
  note: e.note,
});

// ------------------------------------------------- Inventory adjustments (constancia)
export const rowToInventoryAdjustment = (r: Row): InventoryAdjustment => ({
  id: str(r.id),
  date: str(r.date),
  productId: str(r.product_id),
  productName: str(r.product_name),
  qtyBefore: num(r.qty_before),
  qtyAfter: num(r.qty_after),
  delta: num(r.delta),
  reason: str(r.reason),
  userName: str(r.user_name),
  userId: r.user_id ? str(r.user_id) : undefined,
});

export const inventoryAdjustmentToRow = (a: InventoryAdjustment): Row => ({
  date: a.date || new Date().toISOString(),
  product_id: a.productId || null,
  product_name: a.productName,
  qty_before: a.qtyBefore,
  qty_after: a.qtyAfter,
  delta: a.delta,
  reason: a.reason,
  user_name: a.userName,
});

export const roleFromProfile = (r: Row | null): Role =>
  r && str(r.role) === "admin" ? "admin" : "asesor";

// -------------------------------------------------------- Import log
const toArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

export const rowToImportLog = (r: Row): ImportLog => {
  const created = (r.created_ids && typeof r.created_ids === "object" ? r.created_ids : {}) as Row;
  const summary = (r.summary && typeof r.summary === "object" ? r.summary : {}) as Row;

  const createdProducts: ImportProductRef[] = toArr(created.products).map((p) => {
    const row = (p ?? {}) as Row;
    return { id: str(row.id), name: str(row.name), sku: str(row.sku) };
  });
  const createdClients: ImportClientRef[] = toArr(created.clients).map((c) => {
    const row = (c ?? {}) as Row;
    return { id: str(row.id), name: str(row.name) };
  });
  const createdSales: ImportSaleRef[] = toArr(created.sales).map((s) => {
    const row = (s ?? {}) as Row;
    return {
      id: str(row.id),
      productName: str(row.productName),
      clientName: str(row.clientName),
      qty: num(row.qty),
      total: num(row.total),
    };
  });

  const inventoryRestore: ImportInventorySnapshot[] = toArr(r.inventory_restore).map((i) => {
    const row = (i ?? {}) as Row;
    const beforeRaw = row.before;
    return {
      productId: str(row.productId),
      before:
        beforeRaw && typeof beforeRaw === "object" ? rowToInventory(beforeRaw as Row) : null,
    };
  });

  return {
    id: str(r.id),
    source: str(r.source) || "amway_xlsx",
    fileName: str(r.file_name),
    createdAt: str(r.created_at),
    undone: Boolean(r.undone),
    summary: {
      products: num(summary.products),
      clients: num(summary.clients),
      sales: num(summary.sales),
      inventory: num(summary.inventory),
      receivables: num(summary.receivables),
      expenses: num(summary.expenses),
      consignment: num(summary.consignment),
    },
    createdProducts,
    createdClients,
    createdSales,
    createdReceivableIds: toArr(created.receivables).map((v) => str(v)),
    createdExpenseIds: toArr(created.expenses).map((v) => str(v)),
    createdMovementIds: toArr(created.movements).map((v) => str(v)),
    inventoryRestore,
    previousWalletBalances: toWalletBalances(r.previous_wallet_balances),
    warnings: toArr(r.warnings).map((w) => str(w)),
  };
};

export const importLogToRow = (l: ImportLog): Row => ({
  source: l.source,
  file_name: l.fileName,
  summary: l.summary,
  created_ids: {
    products: l.createdProducts,
    clients: l.createdClients,
    sales: l.createdSales,
    receivables: l.createdReceivableIds,
    expenses: l.createdExpenseIds,
    movements: l.createdMovementIds,
  },
  inventory_restore: l.inventoryRestore,
  previous_wallet_balances: l.previousWalletBalances,
  warnings: l.warnings,
  undone: l.undone,
});